import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import { ShoppingCart, ArrowLeft, Check, AlertTriangle } from 'lucide-react';
import { resolveImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState(false);
  const { addToCart, calculatePrice } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product && quantity > 0) {
      addToCart(product, quantity);
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2000);
    }
  };

  const getCurrentPrice = () => {
    if (!product) return 0;
    return calculatePrice(product.price_tiers, quantity);
  };

  const getTotalPrice = () => {
    return getCurrentPrice() * quantity;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Product not found</h2>
          <Link to="/products" className="text-blue-600 hover:underline">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <Link
        to="/products"
        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8"
      >
        <ArrowLeft size={20} />
        Back to Products
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Product Image */}
        <div>
          <div className="relative bg-gray-100 rounded-lg h-96 overflow-hidden">
            <img
              src={resolveImageUrl(product.image_url)}
              alt={product.full_name}
              className={`w-full h-full object-cover ${!product.in_stock ? 'grayscale' : ''}`}
            />
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg">
              <h2 className="text-3xl font-bold text-blue-900">{product.name}</h2>
              <p className="text-blue-600 text-lg font-semibold">{product.weight}</p>
            </div>
            {!product.in_stock && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <span className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold text-xl tracking-wider shadow-2xl">OUT OF STOCK</span>
              </div>
            )}
          </div>
        </div>

        {/* Product Details */}
        <div>
          <h1 className="text-4xl font-bold mb-4" data-testid="product-detail-title">{product.full_name}</h1>
          <p className="text-gray-600 mb-6 text-lg">{product.description}</p>

          {/* Price Tiers Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h3 className="font-semibold text-lg">Volume Pricing</h3>
            </div>
            <div className="divide-y">
              {product.price_tiers.map((tier, index) => (
                <div
                  key={index}
                  className="px-4 py-3 flex justify-between items-center price-tier-row"
                  data-testid={`price-tier-${tier.quantity}`}
                >
                  <span className="font-medium">
                    {tier.quantity}+ {tier.quantity === 1 ? 'piece' : 'pieces'}
                  </span>
                  <span className="text-xl font-bold text-blue-600">
                    ${tier.price.toFixed(2)} each
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Quantity Selector */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Quantity</label>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="border border-gray-300 rounded-lg px-4 py-2 w-32 text-lg"
                data-testid="quantity-input"
              />
              <div className="text-gray-600">
                <p className="text-sm">Price per unit: <span className="font-semibold">${getCurrentPrice().toFixed(2)}</span></p>
                <p className="text-lg font-bold text-blue-600">Total: ${getTotalPrice().toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* Add to Cart Button */}
          {!product.in_stock ? (
            <div className="w-full bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-lg font-semibold text-base flex items-center justify-center gap-2" data-testid="oos-banner">
              <AlertTriangle size={20} />
              Currently out of stock — please check back soon or contact us for availability.
            </div>
          ) : (
            <button
              onClick={handleAddToCart}
              className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 btn-primary"
              data-testid="add-to-cart-button"
            >
              {addedToCart ? (
                <>
                  <Check size={24} />
                  Added to Cart!
                </>
              ) : (
                <>
                  <ShoppingCart size={24} />
                  Add to Cart
                </>
              )}
            </button>
          )}

          {/* Product Features */}
          <div className="mt-8 bg-blue-50 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-3">Product Features</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <Check size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span>100% Virgin Refrigerant</span>
              </li>
              <li className="flex items-start gap-2">
                <Check size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Meets or Exceeds ARI-700 Standards</span>
              </li>
              <li className="flex items-start gap-2">
                <Check size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Fast & Free Shipping</span>
              </li>
              <li className="flex items-start gap-2">
                <Check size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Volume Discounts Available</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;